import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteConfig } from '@/mocks/siteConfig';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-secondary-400">
              <i className="ri-file-unknow-line text-4xl"></i>
            </div>
            <h1 className="text-xl font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-secondary-500 mb-6">该文章可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.date,
        image: article.image,
        publisher: {
          '@type': 'Organization',
          name: siteConfig.name,
        },
        url: `${siteConfig.url}/article/${article.slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: siteConfig.url,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: categoryInfo ? categoryInfo.name : '分类',
            item: `${siteConfig.url}/category/${article.category}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: article.title,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - ${siteConfig.name}`}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryInfo ? categoryInfo.name : '分类', path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 pb-14">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 whitespace-nowrap hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {categoryInfo ? categoryInfo.name : article.category}
                </Link>
                <span className="text-xs text-secondary-500">{article.date}</span>
                <span className="text-xs text-secondary-400">· {article.readTime}</span>
                <span className="text-xs text-secondary-400">· {article.author}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-secondary-500 leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-500 bg-secondary-50 px-2 py-0.5 rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-10 rounded-lg overflow-hidden aspect-[16/9]">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - ${siteConfig.name}`}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="text-sm md:text-base text-foreground-800 leading-relaxed
              [&_h3]:text-lg [&_h3]:font-heading [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-semibold
              [&_p]:text-secondary-700 [&_p]:leading-relaxed [&_p]:mb-4
              [&_strong]:text-foreground-900 [&_strong]:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {relatedArticles.length > 0 && (
              <section className="mt-16 pt-10 border-t border-background-200/70">
                <h3 className="text-lg font-heading text-foreground-900 mb-6">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} {...ra} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}